import type { NodeModel } from '@minoru/react-dnd-treeview';
import { useCallback, useEffect, useMemo, useState } from 'react';

import useAppDispatch from '~hooks/useAppDispatch';
import usePermissions from '~hooks/usePermissions';
import { useEditmodeMenu, useUpdateMenu } from '~services/api/hooks/menu.hooks';
import { insertItemIntoArrayAtPosition } from '~services/immutable';
import { fetchMenuElements } from '~store/thunks/menu';
import type { CustomData } from './types';
import { createNode, generateTreeDataFromMenuElements, sanitizeParentId, sanitizeUuid } from './utils';

export default function useTreeData(isEditAllowed: boolean) {
  const dispatch = useAppDispatch();

  const { hasRootAccessPermission, hasManageMenuElementsPermission, hasManageMenuGrantsPermission } = usePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [treeData, setTreeData] = useState<NodeModel<CustomData>[]>([]);
  const [initialTreeData, setInitialTreeData] = useState(treeData);
  const [changes, setChanges] = useState<MenuElementChange[]>([]);
  const [isUpdateSuccessful, setIsUpdateSuccessful] = useState<boolean>(undefined);

  // Define: state update for notifing user about successful update
  const updateSuccess = useCallback((success: boolean) => {
    setIsUpdateSuccessful(success);
    setTimeout(() => {
      setIsUpdateSuccessful(undefined);
    }, 5000);
  }, []);

  // Define: fetch menu data query
  const { data: menuData, isLoading: menuIsLoading, refetch: refetchMenuForUpdate } = useEditmodeMenu();

  // Define: update menu data query
  const { mutate: updateMenu, isPending: isUpdating } = useUpdateMenu({
    onSuccess: () => {
      updateSuccess(true);
      void dispatch(fetchMenuElements());
    },
    onError: () => {
      updateSuccess(false);
    },
  });

  // Trigger: generate tree data once data and permissions are loaded
  useEffect(() => {
    if (menuData) {
      // Generate tree data
      const treeData = generateTreeDataFromMenuElements(menuData, isEditAllowed, {
        rootAccess: hasRootAccessPermission,
        manageMenuElements: hasManageMenuElementsPermission,
        manageMenuGrants: hasManageMenuGrantsPermission,
      });
      // Set tree data to work with
      setTreeData(treeData);
      // Store original tree data to provide reset function
      setInitialTreeData(treeData);
      // reset changes
      setChanges([]);
      // change loading state
      setIsLoading(false);
    }
  }, [
    hasManageMenuElementsPermission,
    hasManageMenuGrantsPermission,
    hasRootAccessPermission,
    isEditAllowed,
    menuData,
    menuIsLoading,
  ]);

  // Trigger: Refetch data and permissions when isEditAllowed (user-lock) changes
  useEffect(() => {
    void refetchMenuForUpdate();
  }, [refetchMenuForUpdate, isEditAllowed]);

  // Handler: save changes
  const handleOnSave = useCallback(() => {
    if (changes.length) {
      void updateMenu(changes);
    }
  }, [changes, updateMenu]);

  // Handler: reset changes
  const handleOnReset = useCallback(() => {
    setTreeData(initialTreeData);
    setChanges([]);
  }, [initialTreeData]);

  // Handler: add node
  const handleAddNode = useCallback((parentId: NodeModel['id'], type: CustomData['type']) => {
    // Create new node to be added to the tree
    const newNode = createNode(parentId, type);
    setTreeData(treeData => [...treeData, newNode]);
    const change: MenuElementChangeAdd = {
      action: 'add',
      type,
      name: newNode.text,
      parentId: sanitizeParentId(parentId),
      uuid: sanitizeUuid(newNode.id),
    };
    setChanges(changes => insertItemIntoArrayAtPosition(changes, change));
  }, []);

  // Handler: remove node
  const handleRemoveNode = useCallback((nodeId: NodeModel['id']) => {
    setTreeData(treeData => treeData.filter(node => node.id !== nodeId));
    const change: MenuElementChangeDelete = {
      action: 'delete',
      uuid: sanitizeUuid(nodeId),
    };
    setChanges(changes => insertItemIntoArrayAtPosition(changes, change));
  }, []);

  // Handler: renamve node
  const handleRenameNode = useCallback((nodeId: NodeModel['id'], newName: string) => {
    setTreeData(treeData =>
      treeData.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            text: newName,
            data: {
              ...node.data,
              changed: true,
            },
          };
        }
        return node;
      }),
    );
    const change: MenuElementChangeRename = {
      action: 'rename',
      name: newName,
      uuid: sanitizeUuid(nodeId),
    };
    setChanges(changes => insertItemIntoArrayAtPosition(changes, change));
  }, []);

  // Handler: move node
  const handleMoveNode = useCallback(
    (tempTree: NodeModel<CustomData>[], nodeId: NodeModel['id'], parentId: NodeModel['id'], position = 0) => {
      // Modify tree to add 'changed' information
      const newTree = tempTree.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              changed: true,
            },
          };
        }
        return node;
      });
      setTreeData(newTree);

      const change: MenuElementChangeMove = {
        action: 'move',
        newParentId: sanitizeParentId(parentId),
        position,
        uuid: sanitizeUuid(nodeId),
      };
      setChanges(changes => insertItemIntoArrayAtPosition(changes, change));
    },
    [],
  );

  return useMemo(
    () => ({
      isLoading,
      treeData,
      setTreeData,
      updateSuccess: isUpdateSuccessful,
      changes,
      onSave: handleOnSave,
      onReset: handleOnReset,
      addNode: handleAddNode,
      removeNode: handleRemoveNode,
      renameNode: handleRenameNode,
      moveNode: handleMoveNode,
      isProcessing: isUpdating,
    }),
    [
      isLoading,
      treeData,
      isUpdateSuccessful,
      changes,
      handleOnSave,
      handleOnReset,
      isUpdating,
      handleAddNode,
      handleRemoveNode,
      handleRenameNode,
      handleMoveNode,
    ],
  );
}
